import * as Sentry from '@sentry/react';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import { t } from 'i18next';
import mixpanel from 'mixpanel-browser';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { useNotifications } from './notifications';
import { resetStores } from './reset';
import type { SessionStores } from './session';
import type { HostAvailableTeam, HostUser } from './session-types';

export type AuthState = {
  hasUserBeenLoaded: boolean;
  data: HostUser | null;
  /** The user the rest of the app's state was loaded for, see `loadUser`. */
  sessionUserId: number | null;
  availableTeamsAdvanced: HostAvailableTeam[];
  pendingSearches: number;
  loadUser(force?: boolean): Promise<void>;
  changeSite(teamId: string | number): Promise<void>;
  getAvailableTeams(): Promise<void>;
  searchAvailableTeamsAdvanced(search: string): Promise<void>;
  logout(): Promise<void>;
  reset(): void;
};

export type AuthStore = UseBoundStore<StoreApi<AuthState>>;

function initAnalytics(user: HostUser): void {
  try {
    if (user.currentTeam?.slugged_name) {
      Sentry.setTag('site-id', user.currentTeam.slugged_name);
    }
    if (user.intercomOptions) {
      window.Intercom('boot', user.intercomOptions);
    }
    if (!user.user.is_spark_staff) {
      window._hsq = window._hsq || [];
      window._hsq.push(['identify', { email: user.user.email }]);
      try {
        mixpanel.identify(String(user.user.id));
      } catch {
        // Silently fail if Mixpanel is not initialised.
      }
    }
  } catch (err) {
    console.error(err);
  }
}

/** Undo {@link initAnalytics}, so the next user on this page load starts as themselves. */
function resetAnalytics(): void {
  try {
    Sentry.setTag('site-id', undefined);
    // Intercom holds the signed out user's conversations in its own cookie until it is shut down,
    // so without this the next user booted here inherits them.
    window.Intercom?.('shutdown');
    try {
      mixpanel.reset();
    } catch {
      // Silently fail if Mixpanel is not initialised.
    }
  } catch (err) {
    console.error(err);
  }
}

export function createAuthStore(apiHandler: ApiHandlerContract, stores: SessionStores): AuthStore {
  const initialState = {
    hasUserBeenLoaded: false,
    data: null,
    sessionUserId: null,
    availableTeamsAdvanced: [],
    pendingSearches: 0,
  } satisfies Partial<AuthState>;

  return create<AuthState>()((set, get) => ({
    ...initialState,

    async loadUser(force) {
      if (get().hasUserBeenLoaded && !force) {
        return;
      }

      let data: HostUser | null = null;
      let hasUserBeenLoaded = true;
      try {
        // A 204 (no active session) resolves successfully with an undefined body - normalize it
        // to null so callers never see `undefined`.
        data = (await apiHandler.fetch<HostUser>({ method: 'GET', path: '/user' })) ?? null;
      } catch (err) {
        console.error(err);
        // A refused request answers the question (logged out); one that never arrived doesn't,
        // so leave it unloaded and let the next caller ask again.
        hasUserBeenLoaded = err instanceof ApiResponseError;
      }

      set({ data, hasUserBeenLoaded });

      const userId = data?.user.id ?? null;
      // One page load holds one session's data. When the user behind it changes - the session
      // expired, or someone signed in in another tab or in place here - every other store still
      // holds the previous user's team, which `logout` never got the chance to clear.
      if (get().sessionUserId !== userId) {
        stores.useEnvStore.getState().reset();
        stores.useSettingsStore.getState().reset();
        set({ sessionUserId: userId });
      }

      if (data) {
        initAnalytics(data);
      }
    },

    async changeSite(teamId) {
      try {
        await apiHandler.fetch({
          method: 'POST',
          path: '/change-site',
          payload: { team_id: teamId },
        });
      } catch (err) {
        const status = err instanceof ApiResponseError ? err.status : undefined;
        if (status === 403) {
          useNotifications
            .getState()
            .notify(
              t('notifications:titles.error'),
              t('features:app.errors.site_forbidden'),
              'error',
            );
        } else if (status === 404) {
          useNotifications
            .getState()
            .notify(
              t('notifications:titles.error'),
              t('features:app.errors.site_not_found'),
              'error',
            );
        }
      }

      await get().loadUser(true);
      // Everything outside `auth` was loaded for the team being left behind.
      stores.useEnvStore.getState().reset();
      stores.useSettingsStore.getState().reset();
    },

    async getAvailableTeams() {
      if (!get().data) {
        return;
      }
      const availableTeams = await apiHandler.fetch<HostAvailableTeam[]>({
        method: 'GET',
        path: '/available-teams',
      });
      set((state) =>
        state.data ? { data: { ...state.data, availableTeams: availableTeams ?? [] } } : {},
      );
    },

    async searchAvailableTeamsAdvanced(search) {
      if (search === '') {
        set({ availableTeamsAdvanced: [] });
        return;
      }

      set((state) => ({ pendingSearches: state.pendingSearches + 1 }));
      try {
        const availableTeamsAdvanced = await apiHandler.fetch<HostAvailableTeam[]>({
          method: 'GET',
          path: `/search-available-teams-advanced?search=${encodeURIComponent(search)}`,
        });
        set({ availableTeamsAdvanced: availableTeamsAdvanced ?? [] });
      } finally {
        set((state) => ({ pendingSearches: state.pendingSearches - 1 }));
      }
    },

    async logout() {
      try {
        await apiHandler.fetch({ method: 'POST', path: '/logout' });
      } catch (err) {
        console.error(err);
      }

      resetAnalytics();
      // Every store, not just this one - the previous team also lives in `env` (which the url is
      // built from) and `settings`, and the previous session's notifications have no business
      // following the user to the login screen.
      resetStores();
    },

    reset() {
      set(initialState);
    },
  }));
}
